/// <amd-module name="Release/Landing/LeftPanel/AllPipelines/AllDefinitionsActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { localeFormat } from "VSS/Core/Util/String";

import { ActionCreatorBase } from "DistributedTask/Flux/Actions";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { Folder, ReleaseDefinition } from "Release/Client/Contracts/Release";
import { IReleaseService } from "Release/Client/Service/Release";
import { AllDefinitionsActions, IPipelineMovedPayload } from "./AllDefinitionsActions";
import { PipelinesActions } from "../PipelinesActions";
import { SelectionActions } from "Selection/SelectionActions";
import { MessageActions } from "Release/Landing/HubMessageBar/MessageActions";
import { MessageBarKeys, RmServiceInstanceId } from "Release/Landing/Constants";

import { FolderCreatedMessage, PipelineMovedMessage } from "Release/Landing/Resources";
import { IMovedRenamedDefinitionProviderData } from "Release/Landing/DataProviderContracts";

export class AllDefinitionsActionCreator extends ActionCreatorBase {
    public static getKey(): string {
        return "all-definitions-actioncreator";
    }

    public initialize(): void {
        this._actions = ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions);
        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
        this._pipelinesActions = ActionsHubManager.GetActionsHub<PipelinesActions>(PipelinesActions);
        this._selectionActions = ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions);
    }

    public loadDefinitionTreeItems(pageContext: IVssPageContext, folderPath: string): void {
    }

    public createFolder(pageContext: IVssPageContext, folderPath: string): void {
        this._getReleaseService(pageContext).createFolder(folderPath).then((folder: Folder) => {
            this._actions.folderCreated.invoke(folder);
            const folderParts = folder.path.split("\\").filter(f => !!f);
            this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: localeFormat(FolderCreatedMessage, folderParts[folderParts.length - 1]) });
        }, (error) => {
            this._handleError(error);
        });
    }

    public renamePipeline(pageContext: IVssPageContext, definitionId: number, name: string): void {
        this._renameOrMovePipelineOrFolder(pageContext, definitionId, undefined, name, undefined).then((updatedData: IMovedRenamedDefinitionProviderData) => {
            this._actions.pipelineRenamed.invoke(updatedData.updatedDefinition);
            this._pipelinesActions.onRenamePipeline.invoke({ pipelineId: definitionId, pipelineName: name });
            this._selectionActions.selectDefinition.invoke({ folderId: "", id: definitionId, noSelection: false });
        }, (error) => {
            this._handleError(error);
        });
    }

    public movePipeline(pageContext: IVssPageContext, definitionId: number, oldParentPath: string, newParentPath: string): void {
        this._renameOrMovePipelineOrFolder(pageContext, definitionId, undefined, undefined, newParentPath).then((updatedData: IMovedRenamedDefinitionProviderData) => {
            this._actions.pipelineMoved.invoke({
                movedPipeline: updatedData.updatedDefinition,
                originalPath: updatedData.oldFolderPath
            } as IPipelineMovedPayload);

            this._pipelinesActions.onMovePipeline.invoke({ pipelineId: definitionId, newFolderPath: updatedData.updatedDefinition.path });
            setTimeout(() => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: localeFormat(PipelineMovedMessage, updatedData.updatedDefinition.name, oldParentPath, newParentPath) }), 200); // Show after selections are complete
        }, (error) => {
            this._handleError(error);
        });
    }

    public renameFolder(pageContext: IVssPageContext, folder: Folder, oldPath: string): void {

        this._renameOrMovePipelineOrFolder(pageContext, undefined, oldPath, undefined, folder.path)
            .then((updatedData: IMovedRenamedDefinitionProviderData) => {
                const folder = updatedData.updatedFolder;
                this._actions.folderRenamed.invoke({ folder: folder, oldPath: oldPath });
                this._pipelinesActions.onRenameFolder.invoke({ folderPath: folder.path, oldFolderPath: oldPath });
                this._selectionActions.selectDefinition.invoke({ folderId: folder.path, id: -1, noSelection: false });
            }, (error) => {
                this._handleError(error);
            });
    }

    private _renameOrMovePipelineOrFolder(pageContext: IVssPageContext, definitionId: number | undefined, folderPath: string | undefined, newName: string | undefined, newFolderPath: string | undefined): Promise<IMovedRenamedDefinitionProviderData | undefined> {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync(renameMovePipelineDataProviderContributionId, {
            definitionId: definitionId,
            folderPath: folderPath,
            newName: newName,
            newPath: newFolderPath
        },
            true,
            RmServiceInstanceId).then((updatedData: IMovedRenamedDefinitionProviderData) => {
                if (!updatedData) {
                    const error = contributionService.getDataProviderExceptions()[renameMovePipelineDataProviderContributionId];
                    if (error) {
                        return Promise.reject(error.message);
                    }
                }

                return Promise.resolve(updatedData);
            });
    }

    private _getReleaseService(pageContext: IVssPageContext): IReleaseService {
        return pageContext.getService<IReleaseService>("IReleaseService");
    }

    private _handleError(error: any): void {
        const errorMessage: string = error ? error.message || error : "";
        this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: errorMessage });
    }

    private _actions: AllDefinitionsActions;
    private _messageActions: MessageActions;
    private _pipelinesActions: PipelinesActions;
    private _selectionActions: SelectionActions;
}

const renameMovePipelineDataProviderContributionId = "ms.vss-releaseManagement-web.rename-move-definition-data-provider";